import { CartAddEvent, ThemeEvents } from '@theme/events';

const pendingForms = new WeakSet();
const lastSubmitTimes = new WeakMap();
const MESSAGE_DURATION = 3000;
const SUBMIT_DEBOUNCE_DELAY = 800;
const FORM_MESSAGE_EVENT = 'theme:form-message';

document.addEventListener('submit', handleSubmit);
document.addEventListener(FORM_MESSAGE_EVENT, (event) => {
  const detail = event instanceof CustomEvent ? event.detail : {};
  const message = typeof detail?.message === 'string' ? detail.message : '';
  const type = detail?.type === 'error' ? 'error' : 'success';
  if (message) showToast(message, type);
});
document.addEventListener(ThemeEvents.cartError, (event) => {
  const message = event.detail?.data?.message || event.detail?.data?.description;
  if (message) showToast(message, 'error');
});
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.data?.didError || event.detail?.data?.enhancedForm) return;
  if (event.detail?.data?.source !== 'product-form-component') return;

  showToast(Theme.translations?.added || 'Added to cart.', 'success');
});

/**
 * @param {SubmitEvent} event
 */
async function handleSubmit(event) {
  if (event.defaultPrevented || !(event.target instanceof HTMLFormElement)) return;

  const form = event.target;
  const formType = getEnhancedFormType(form);
  if (!formType) return;

  event.preventDefault();
  if (pendingForms.has(form)) return;

  if (!form.checkValidity()) {
    form.reportValidity();
    return;
  }

  const now = Date.now();
  const lastSubmitTime = lastSubmitTimes.get(form) || 0;
  if (now - lastSubmitTime < SUBMIT_DEBOUNCE_DELAY) return;
  lastSubmitTimes.set(form, now);

  pendingForms.add(form);
  const submitButton = getSubmitButton(form, event.submitter);
  setFormLoading(form, submitButton, true);

  try {
    if (formType === 'product') {
      const message = await submitProductForm(form);
      showMessage(form, message, 'success');
      showToast(message, 'success');
    } else {
      const result = await submitHtmlForm(form, formType);
      showMessage(form, result.message, result.type);

      if (result.type === 'success') {
        form.reset();
      }
    }
  } catch (error) {
    const message = error instanceof Error && error.message ? error.message : 'Something went wrong. Please try again.';
    showMessage(form, message, 'error');
    showToast(message, 'error');
  } finally {
    pendingForms.delete(form);
    setFormLoading(form, submitButton, false);
  }
}

/**
 * @param {HTMLFormElement} form
 * @returns {'product' | 'contact' | 'customer' | 'comment' | undefined}
 */
function getEnhancedFormType(form) {
  if (!isPostForm(form)) return undefined;
  if (form.closest('product-form-component, cart-discount-component')) return undefined;
  if (form.id === 'cart-form' || form.querySelector('[name="checkout"]')) return undefined;
  if (form.closest('localization-form') || form.querySelector('[name="form_type"][value="storefront_password"]')) {
    return undefined;
  }

  const formType = form.querySelector('[name="form_type"]')?.getAttribute('value');
  const action = getFormAction(form);
  const hasVariantId = Boolean(form.querySelector('input[name="id"]'));

  if ((formType === 'product' || action.includes('/cart/add')) && hasVariantId) return 'product';
  if (formType === 'contact') return 'contact';
  if (formType === 'customer') return 'customer';
  if (formType === 'new_comment') return 'comment';

  return undefined;
}

/**
 * @param {HTMLFormElement} form
 */
function isPostForm(form) {
  return (form.getAttribute('method') || 'get').toLowerCase() === 'post';
}

/**
 * @param {HTMLFormElement} form
 */
function getFormAction(form) {
  return form.getAttribute('action') || window.location.href;
}

/**
 * @param {HTMLFormElement} form
 */
async function submitProductForm(form) {
  const formData = new FormData(form);
  const sections = getCartSectionIds();

  if (sections.length) {
    formData.set('sections', sections.join(','));
    formData.set('sections_url', window.location.pathname);
  }

  const response = await fetch(Theme.routes?.cart_add_url || '/cart/add', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: formData,
  });
  const data = await parseJsonResponse(response);

  if (!response.ok || data.status) {
    throw new Error(getResponseMessage(data) || 'Unable to add this item to cart.');
  }

  const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then((cartResponse) => cartResponse.json())
    .catch(() => undefined);

  form.dispatchEvent(
    new CartAddEvent(cart, String(formData.get('id') || ''), {
      enhancedForm: true,
      source: 'product-form-component',
      itemCount: Number(formData.get('quantity')) || 1,
      sections: data.sections,
    })
  );

  return getResponseMessage(data) || Theme.translations?.added || 'Added to cart.';
}

/**
 * @param {HTMLFormElement} form
 * @param {'contact' | 'customer' | 'comment'} formType
 * @returns {Promise<{ type: 'success' | 'error', message: string }>}
 */
async function submitHtmlForm(form, formType) {
  const response = await fetch(getFormAction(form), {
    method: 'POST',
    headers: {
      Accept: 'text/html',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: new FormData(form),
  });
  const contentType = response.headers.get('content-type') || '';
  let parsed = { type: undefined, message: '' };

  if (contentType.includes('application/json')) {
    const data = await parseJsonResponse(response);
    parsed = {
      type: response.ok && !data.status && !data.errors ? 'success' : 'error',
      message: getResponseMessage(data),
    };
  } else {
    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    parsed = extractServerMessage(documentFragment, formType, response.url);
  }

  if (!response.ok || parsed.type === 'error') {
    throw new Error(parsed.message || 'Please check the form and try again.');
  }

  return {
    type: 'success',
    message: parsed.message || getDefaultSuccessMessage(formType),
  };
}

/**
 * @param {Document} documentFragment
 * @param {'contact' | 'customer' | 'comment'} formType
 * @param {string} responseUrl
 * @returns {{ type: 'success' | 'error' | undefined, message: string }}
 */
function extractServerMessage(documentFragment, formType, responseUrl) {
  const successSelectors = {
    customer: '.email-signup__message[id*="success"], [id*="message-success"], .form-status',
    contact: '.contact-form__success, .custom-page-contact-form__success, [id*="message-success"], .form-status',
    comment: '.blog-post-comments__form-message, [id*="message-success"], .form-status',
  }[formType];
  const errorSelectors = {
    customer: '.email-signup__message[id*="error"], [id*="message-error"], .errors, .form__message',
    contact: '.contact-form__error, .errors, [id*="message-error"], .form__message',
    comment: '.blog-post-comments__form-message[role="alert"], .errors, [id*="message-error"], .form__message',
  }[formType];

  if (formType === 'comment' && responseUrl.includes('comment_posted=true')) {
    return { type: 'success', message: getDefaultSuccessMessage(formType) };
  }

  const error = getCleanText(documentFragment.querySelector(errorSelectors));
  if (error) return { type: 'error', message: error };

  const success = getCleanText(documentFragment.querySelector(successSelectors));
  if (success) return { type: 'success', message: success };

  if (
    responseUrl.includes('customer_posted=true') ||
    responseUrl.includes('contact_posted=true') ||
    responseUrl.includes('comment_posted=true')
  ) {
    return { type: 'success', message: getDefaultSuccessMessage(formType) };
  }

  return { type: undefined, message: '' };
}

/**
 * @param {Element | null} element
 */
function getCleanText(element) {
  return element?.textContent?.replace(/\s+/g, ' ').trim() || '';
}

/**
 * @param {'contact' | 'customer' | 'comment'} formType
 */
function getDefaultSuccessMessage(formType) {
  if (formType === 'comment') return 'Thanks, your comment has been submitted.';
  return formType === 'customer' ? 'Thanks for signing up.' : 'Thanks, your message has been sent.';
}

/**
 * @param {Response} response
 * @returns {Promise<any>}
 */
async function parseJsonResponse(response) {
  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch (_error) {
    return { message: text };
  }
}

/**
 * @param {any} data
 * @returns {string}
 */
function getResponseMessage(data) {
  const message = data?.message || data?.description || data?.error;
  if (typeof message === 'string') return message;

  if (Array.isArray(data?.errors)) {
    return data.errors.filter(Boolean).join(' ');
  }

  if (data?.errors && typeof data.errors === 'object') {
    return Object.values(data.errors)
      .flat()
      .filter(Boolean)
      .join(' ');
  }

  if (typeof data?.errors === 'string') return data.errors;

  return '';
}

function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
    .filter(Boolean);
}

/**
 * @param {HTMLFormElement} form
 * @param {SubmitEvent['submitter']} submitter
 */
function getSubmitButton(form, submitter) {
  if (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) return submitter;
  return form.querySelector('button[type="submit"], input[type="submit"], button:not([type])');
}

/**
 * @param {HTMLFormElement} form
 * @param {HTMLButtonElement | HTMLInputElement | null} submitButton
 * @param {boolean} isLoading
 */
function setFormLoading(form, submitButton, isLoading) {
  form.toggleAttribute('aria-busy', isLoading);

  if (!submitButton) return;

  if (isLoading) {
    submitButton.style.setProperty('--loading-button-color', getComputedStyle(submitButton).color);
    submitButton.dataset.loading = 'true';
    submitButton.setAttribute('aria-disabled', 'true');
    submitButton.disabled = true;
  } else {
    submitButton.style.removeProperty('--loading-button-color');
    submitButton.removeAttribute('data-loading');
    submitButton.removeAttribute('aria-disabled');
    submitButton.disabled = false;
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {string} message
 * @param {'success' | 'error'} type
 */
function showMessage(form, message, type) {
  const existing = form.parentElement?.querySelector(':scope > .theme-form-message[data-form-message]');
  existing?.remove();

  const messageElement = document.createElement('div');
  messageElement.className = 'theme-form-message';
  messageElement.dataset.formMessage = 'true';
  messageElement.dataset.type = type;
  messageElement.role = type === 'error' ? 'alert' : 'status';
  messageElement.textContent = message;
  form.insertAdjacentElement('afterend', messageElement);

  window.setTimeout(() => {
    if (messageElement.isConnected) messageElement.remove();
  }, MESSAGE_DURATION);
}

/**
 * @param {string} message
 * @param {'success' | 'error'} type
 */
function showToast(message, type) {
  let region = document.querySelector('.theme-toast-region');
  if (!region) {
    region = document.createElement('div');
    region.className = 'theme-toast-region';
    region.setAttribute('aria-live', 'polite');
    document.body.appendChild(region);
  }

  const messageElement = document.createElement('div');
  messageElement.className = 'theme-form-message';
  messageElement.dataset.type = type;
  messageElement.textContent = message;

  region.replaceChildren(messageElement);
  window.setTimeout(() => {
    if (messageElement.parentElement === region) messageElement.remove();
  }, MESSAGE_DURATION);
}
